import { AMADEUS, getConfigTenant, isVesperTenant } from "@capone/common";
import { useExperimentsById } from "@capone/experiments";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import {
  B2BSpinner,
  LoadingIndicator,
  TravelerSelectStep,
  TravelerSelectWorkflow,
  VoidWindowNotice,
  useCheckInMinimumAgeValidation,
  getAgeRequirementMessage,
  hasInvalidBirthdate,
} from "halifax";
import React, { useMemo, useState } from "react";
import {
  CallState,
  CLICKED_ADD_TRAVELER,
  ClickedAddTravelerProperties,
  IPerson,
  ITravelerStepErrors,
  PersonId,
  SELECT_TRAVELERS,
} from "redmond";

import { config } from "../../../../../api/config";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import {
  addTrackingProperties,
  useExperiments,
} from "../../../../../context/experiments";
import {
  MULTIROOM_TOOLTIP_TEXT,
  MULTIROOM_TOOLTIP_TITLE,
  TRAVELER_SELECT_DROPDOWN_DESCRIPTION,
  TRAVELER_SELECT_DROPDOWN_TITLE,
} from "../HotelTravelerSelectDropdownForm/constants";
import { MobileHotelBookPassengerSelectionConnectorProps } from "./container";
import "./styles.scss";
import * as constants from "./textConstants";

export interface IMobileHotelBookPassengerSelectionProps
  extends MobileHotelBookPassengerSelectionConnectorProps {
  progress: TravelerSelectStep;
  setProgress: (progress: TravelerSelectStep) => void;
  onProgressChange?: (step: TravelerSelectStep) => void;
  onContinue?: (travelersChanged: string[]) => void;
  onGoBack?: (travelersChanged: string[]) => void;
  className?: string;
  selectionScreenHeaderElement?: JSX.Element;
  onReviewStep?: boolean;
  disabled?: boolean;
  combinedStep?: boolean;
  showErrors?: ITravelerStepErrors;
  setShowErrors?: (showErrors: ITravelerStepErrors) => void;
  saveButtonClicked?: boolean;
  useLocalId?: boolean;
  showHotelLoyaltySection?: boolean;
  showVoidWindowNotice: boolean;
}

const Header = ({
  roomsCount,
  isAmadeusProvider,
  isMultiroomAmadeus,
  minimumCheckInAge,
}: {
  roomsCount: number;
  isAmadeusProvider: boolean;
  isMultiroomAmadeus: boolean;
  minimumCheckInAge?: number;
}) => {
  const getSubtitle = () => {
    const baseSubtitle = TRAVELER_SELECT_DROPDOWN_DESCRIPTION(
      roomsCount,
      isAmadeusProvider,
      isMultiroomAmadeus,
    );

    return minimumCheckInAge != null
      ? `${baseSubtitle} ${getAgeRequirementMessage(minimumCheckInAge)}`
      : baseSubtitle;
  };

  return (
    <>
      <Box className="mobile-hotel-book-passenger-selection-title-container">
        <Typography component="h1" className="title">
          {TRAVELER_SELECT_DROPDOWN_TITLE(
            roomsCount,
            isAmadeusProvider,
            isMultiroomAmadeus,
          )}
        </Typography>
        <Typography variant="body2" className="subtitle">
          {getSubtitle()}
        </Typography>
      </Box>
      {!isAmadeusProvider && isMultiroomAmadeus && (
        <Accordion square className="multiroom-accordian">
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-content"
            id="panel-header"
          >
            <Typography className="accordian-title" variant="subtitle2">
              {MULTIROOM_TOOLTIP_TITLE}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography className="accordian-subtitle" variant="subtitle2">
              {MULTIROOM_TOOLTIP_TEXT}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
export const MobileHotelBookPassengerSelection = (
  props: IMobileHotelBookPassengerSelectionProps,
) => {
  const {
    className,
    travelers,
    progress,
    selectionScreenHeaderElement,
    setProgress,
    onProgressChange,
    onContinue,
    onGoBack,
    setUserSelectedPassengerIds,
    updateUserPassenger,
    deleteUserPassenger,
    userPassengerCallState,
    onReviewStep,
    disabled,
    combinedStep,
    showErrors,
    setShowErrors,
    saveButtonClicked,
    selectedTravelerId,
    roomsCount,
    chosenProduct,
    fetchCorpUserPassengers,
    showHotelLoyaltySection,
    showVoidWindowNotice,
    minimumCheckInAge,
    checkInDate,
  } = props;
  const isVesper = isVesperTenant(getConfigTenant());
  const [travelersChanged, setTravelersChanged] = useState<string[]>([]);

  const isAmadeusProvider = chosenProduct?.providerName === AMADEUS;

  const expState = useExperiments();

  const selectedTravelerIds = useMemo(
    () => travelers.filter((t) => t.id === selectedTravelerId).map((t) => t.id),
    [selectedTravelerId, travelers],
  );

  const { disabledTravelerIds, sortedTravelers } =
    useCheckInMinimumAgeValidation({
      minimumAge: minimumCheckInAge,
      travelers,
      selectedTravelerId: selectedTravelerIds[0],
      setSelectedTravelerIds: (travelerIds: string[]) => {
        setUserSelectedPassengerIds({
          userSelectedPassengerIds: travelerIds,
        });
      },
      checkInDate,
    });

  const isMultiroomAmadeus =
    useExperimentsById("corp-amadeus-multiroom")?.variant === "available";

  React.useEffect(() => {
    fetchCorpUserPassengers();
  }, []);

  React.useEffect(() => {
    if (
      travelers.length === 1 &&
      !disabledTravelerIds.includes(travelers[0].id) &&
      !hasInvalidBirthdate(travelers[0].dateOfBirth)
    ) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: [travelers[0].id],
      });
    }
  }, [travelers, disabledTravelerIds]);

  const [localTravelerIds, setLocalTravelerIds] =
    useState<string[]>(selectedTravelerIds);

  const handleContinue = () => {
    onContinue && onContinue(travelersChanged);
    setLocalTravelerIds(selectedTravelerIds);
    setTravelersChanged([]);
  };

  const handleGoBack = () => {
    onGoBack && onGoBack(travelersChanged);
    setLocalTravelerIds(selectedTravelerIds);
  };

  const getTravelerInfoSubtitle = () => {
    if (!onReviewStep) return "";

    const baseSubtitle = TRAVELER_SELECT_DROPDOWN_DESCRIPTION(
      roomsCount,
      isAmadeusProvider,
      isMultiroomAmadeus,
    );

    if (minimumCheckInAge != null) {
      return `${baseSubtitle} ${getAgeRequirementMessage(minimumCheckInAge)}`;
    }

    return baseSubtitle;
  };

  return (
    <div>
      {userPassengerCallState === CallState.InProcess ? (
        <LoadingIndicator
          indicatorSize="small"
          indicator={B2BSpinner}
          message={
            userPassengerCallState === CallState.InProcess
              ? constants.UPDATE_TEXT
              : constants.VALIDATE_TEXT
          }
        />
      ) : (
        <TravelerSelectWorkflow
          displayModalSubtitle
          showGenderField
          showNationalityField
          hideDelete={isMultiroomAmadeus ? true : false}
          singleTravelerWorkflow={
            isMultiroomAmadeus && isAmadeusProvider ? false : true
          }
          buttonClassName="b2b"
          className={clsx(
            "mobile-hotel-book-passenger-selection-root",
            className,
            {
              "combined-step": combinedStep,
              redesigned: isVesper,
            },
          )}
          disabled={disabled}
          errorMessage={constants.ADD_TRAVELER_ERROR_MESSAGE}
          handleDeletePassenger={(travelerId: PersonId) => {
            const newTravelerIdList = localTravelerIds.filter(
              (id) => id !== travelerId,
            );
            setLocalTravelerIds(newTravelerIdList);
            deleteUserPassenger({ personId: travelerId });
          }}
          handleUpdatePassenger={(traveler: IPerson, _, __, isNewTraveler) => {
            if (isNewTraveler)
              setLocalTravelerIds([...localTravelerIds, traveler.id]);

            updateUserPassenger(
              {
                person: traveler,
              },
              traveler.driverLicense !== undefined,
            );
          }}
          isMobile
          onContinue={handleContinue}
          onGoBack={handleGoBack}
          onProgressChange={onProgressChange}
          progress={progress}
          saveButtonClicked={saveButtonClicked}
          selectedTravelerIds={selectedTravelerIds}
          selectionScreenHeaderElement={selectionScreenHeaderElement}
          setProgress={setProgress}
          setSelectedTravelerIds={(travelerIds: string[]) => {
            trackEvent({
              eventName: SELECT_TRAVELERS,
              properties: {},
            });

            const changedTravelersList = travelerIds.filter(
              (id) => !localTravelerIds.includes(id),
            );

            setTravelersChanged(changedTravelersList);
            setUserSelectedPassengerIds({
              userSelectedPassengerIds: travelerIds,
            });
          }}
          setShowErrors={setShowErrors}
          showErrors={showErrors}
          titles={{
            travelerInfoTitle: onReviewStep
              ? constants.TRAVELER_INFORMATION_TITLE(isMultiroomAmadeus)
              : "",
            travelerInfoSubtitle: getTravelerInfoSubtitle(),
            frequentFlyerTitle: constants.FREQUENT_FLYER_TITLE,
            additionalInfoTitle: constants.ADDITIONAL_INFO_TITLE,
            adultTitle: constants.ADULT_TITLE,
            childTitle: constants.CHILD_TITLE,
            infantSeatTitle: constants.INFANT_SEAT_TITLE,
            infantLapTitle: constants.INFANT_LAP_TITLE,
            addTravelers: onReviewStep
              ? TRAVELER_SELECT_DROPDOWN_TITLE(roomsCount, isAmadeusProvider)
              : constants.ADD_TRAVELER_INFO_TEXT,

            completeTravelerProfileTitle: constants.COMPLETE_PROFILE_TITLE,
            completeTravelerProfileSubtitle:
              constants.COMPLETE_PROFILE_SUBTITLE,
            editTravelerTitle: constants.EDIT_TRAVELER_TITLE,
            editTravelerSubtitle: constants.EDIT_TRAVELER_SUBTITLE,
          }}
          travelers={sortedTravelers}
          disabledTravelerIds={disabledTravelerIds}
          minimumCheckInAge={minimumCheckInAge}
          checkInDate={checkInDate}
          wrapButton={!onReviewStep}
          onClickAddNewTraveler={() => {
            trackEvent({
              eventName: CLICKED_ADD_TRAVELER,
              properties: addTrackingProperties(expState.trackingProperties, {
                entry_type: "checkout",
              } as ClickedAddTravelerProperties),
            });
          }}
          tenant={config.TENANT}
          trackEvent={trackEvent}
          maxTravelerCount={
            isMultiroomAmadeus && isAmadeusProvider ? roomsCount : undefined
          }
          customHeader={
            <Header
              roomsCount={roomsCount}
              isAmadeusProvider={isAmadeusProvider}
              isMultiroomAmadeus={isMultiroomAmadeus}
              minimumCheckInAge={minimumCheckInAge}
            />
          }
          showHotelLoyaltySection={showHotelLoyaltySection}
          bottomContent={
            showVoidWindowNotice ? <VoidWindowNotice /> : undefined
          }
        />
      )}
    </div>
  );
};
